import React, { useState } from "react";
import { DatePicker, Button } from "antd";
import styled from "styled-components";
import { PlusCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useMessage } from "../../../uteis/mensagens/useMessage";

const Wrapper = styled.div`
  margin-top: 2rem;
`;

const Titulo = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: #222;
`;

const InputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.2rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BotaoAdd = styled(Button)`
  display: flex;
  align-items: center;
`;

const Lista = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-top: 1rem;
`;

const Item = styled.li`
  background: #f6faff;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  margin-bottom: 0.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #333;
`;

const DeleteIcon = styled(DeleteOutlined)`
  cursor: pointer;
  color: #c00;
  margin-left: 1rem;

  &:hover {
    color: #a00;
  }
`;

interface Periodo {
  inicio: string;
  fim: string;
}

interface Props {
  onChange: (periodos: Periodo[]) => void;
}

const SelecaoPeriodos = ({ onChange }: Props) => {
  const [inicio, setInicio] = useState<dayjs.Dayjs | null>(null);
  const [fim, setFim] = useState<dayjs.Dayjs | null>(null);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const exigePeriodo = false; // exige pelo menos um período
  const { showError, showWarning, contextHolder } = useMessage();

  const adicionarPeriodo = () => {
    if (!inicio || !fim) return;

    const novo: Periodo = {
      inicio: inicio.format("YYYY-MM-DD"),
      fim: fim.format("YYYY-MM-DD"),
    };

    const hoje = dayjs().startOf("day");

    // validações básicas
    if (fim.isBefore(hoje)) {
      showWarning(
        "O período incluído já passou. Por favor, selecione datas futuras."
      );
      return;
    }

    if (!fim.isAfter(inicio, "day")) {
      showWarning("A data final deve ser posterior à data de início.");
      return;
    }

    // valida diferença máxima de 7 dias
    const diffDias = fim.diff(inicio, "day");
    if (diffDias > 7) {
      showWarning("O período não pode ser maior que 7 dias!");
      return;
    }

    // evitar duplicata
    const existe = periodos.some(
      (p) => p.inicio === novo.inicio && p.fim === novo.fim
    );
    if (existe) {
      showWarning("Esse período já foi adicionado.");
      return;
    }

    // adiciona se passou por todas as validações
    const atualizados = [...periodos, novo];
    setPeriodos(atualizados);
    onChange(atualizados);
    setInicio(null);
    setFim(null);
  };

  const remover = (idx: number) => {
    const atualizados = [...periodos];
    atualizados.splice(idx, 1);
    setPeriodos(atualizados);
    onChange(atualizados);
  };

  return (
    <Wrapper>
      {contextHolder}
      <Titulo>📅 Quais períodos você gostaria de alugar ou trocar?</Titulo>

      <InputArea>
        <Row>
          <DatePicker
            placeholder="Data de início"
            format="DD/MM/YYYY"
            value={inicio}
            onChange={(date) => {
              setInicio(date);
              setFim(null);
            }}
            style={{ width: "100%" }}
            required={exigePeriodo}
            getPopupContainer={() => document.body}
            disabledDate={(current) => {
              const hoje = dayjs().startOf("day");
              return current && current.isBefore(hoje, "day");
            }}
          />

          <DatePicker
            placeholder="Data de fim"
            format="DD/MM/YYYY"
            value={fim}
            onChange={(date) => setFim(date)}
            style={{ width: "100%" }}
            getPopupContainer={() => document.body}
            defaultPickerValue={inicio || undefined}
            disabledDate={(current) => {
              const hoje = dayjs().startOf("day");

              if (!current) return false;

              return (
                current.isBefore(hoje, "day") || // bloqueia antes de hoje
                (inicio ? current.isBefore(inicio, "day") : false) // bloqueia antes do início
              );
            }}
          />

          <BotaoAdd
            onClick={adicionarPeriodo}
            disabled={!inicio || !fim}
            type="primary"
          >
            <PlusCircleOutlined /> Adicionar
          </BotaoAdd>
        </Row>
      </InputArea>

      {periodos.length > 0 && (
        <Lista>
          {periodos.map((p, i) => (
            <Item key={i}>
              {dayjs(p.inicio).format("DD/MM/YYYY")} até{" "}
              {dayjs(p.fim).format("DD/MM/YYYY")}
              <DeleteIcon onClick={() => remover(i)} />
            </Item>
          ))}
        </Lista>
      )}
    </Wrapper>
  );
};

export default SelecaoPeriodos;
